import { isAxiosError } from 'axios'

const EMAIL_CONNECTION_AUTH_ERROR_CODES = ['AUTHENTICATION_ERROR', 'MAIL_AUTH_NYLAS_EXPIRED'] as const

type EmailConnectionAuthErrorCode = (typeof EMAIL_CONNECTION_AUTH_ERROR_CODES)[number]

interface EmailConnectionAuthFailure {
    code: EmailConnectionAuthErrorCode
    message?: string
}

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null

/**
 * A failed request, read by shape: axios's own `isAxiosError` flag (what `isAxiosError()` checks), or a
 * `{ response }` object as tests and hand-built rejections pass. By shape rather than through the axios
 * module so a mocked axios does not change what these helpers answer.
 */
const isRequestError = (error: unknown): error is Record<string, unknown> => isRecord(error) && (error.isAxiosError === true || isRecord(error.response))

const requestResponse = (error: unknown): Record<string, unknown> | undefined =>
    isRequestError(error) && isRecord(error.response) ? error.response : undefined

/** The JSON body of a failed request, when the server answered with one. */
const serverBody = (error: unknown): unknown => requestResponse(error)?.data

const serverField = (body: unknown, key: string): string | undefined => {
    const value = isRecord(body) ? body[key] : undefined
    return typeof value === 'string' ? value : undefined
}

const isEmailConnectionAuthErrorCode = (value: unknown): value is EmailConnectionAuthErrorCode =>
    typeof value === 'string' && EMAIL_CONNECTION_AUTH_ERROR_CODES.some((code) => code === value)

export const getEmailConnectionAuthFailureFromResponse = (data: unknown): EmailConnectionAuthFailure | null => {
    if (!isRecord(data) || !isEmailConnectionAuthErrorCode(data.error)) return null

    return {
        code: data.error,
        ...(typeof data.message === 'string' && data.message.trim() ? { message: data.message } : {}),
    }
}

export const getEmailConnectionAuthFailure = (error: unknown): EmailConnectionAuthFailure | null => {
    if (!isAxiosError(error)) return null
    return getEmailConnectionAuthFailureFromResponse(error.response?.data)
}

export const isAbortError = (error: unknown): boolean => {
    if (error instanceof DOMException && error.name === 'AbortError') return true
    if (error instanceof Error && (error.name === 'AbortError' || error.name === 'CanceledError')) return true
    if (isAxiosError(error) && (error.code === 'ERR_CANCELED' || error.name === 'CanceledError')) return true
    return false
}

export const getUserErrorMessage = (error: unknown): string => {
    if (isAbortError(error)) return ''
    if (isAxiosError(error)) {
        return error.response?.data?.message || error.message
    }
    if (error instanceof Error) {
        return error.message
    }
    return String(error)
}

/**
 * The HTTP status of a failed request, or 0 when the call never reached a response (network, abort).
 *
 * Exists for analytics: the server's error MESSAGE is free text and unbounded, so it cannot be an event
 * property, while the status is a closed set that still separates "refused by a business rule" (409),
 * "not allowed" (403) and "broke" (5xx).
 */
export const getErrorStatus = (error: unknown): number => {
    const response = requestResponse(error)
    return response && typeof response.status === 'number' ? response.status : 0
}

/**
 * The message the SERVER sent, or undefined — never a client-side one.
 *
 * `getUserErrorMessage` falls back to `error.message`, which for a bug in the calling code is a stack
 * message ("Cannot read properties of undefined") and for a dead network is axios's own "Request failed
 * with status code 502". Neither is copy to put in front of a dispatcher. Use this where the point is to
 * relay an explanation the server actually wrote (a 403 naming the missing permission, a 409 naming the
 * conflicting load) and to fall back to your own wording for everything else.
 * Most endpoints use `message`; the driver invite endpoints put their human-readable refusal in `error`,
 * so those callers opt into that field without changing other screens' error handling.
 */
export const getServerErrorMessage = (error: unknown, field: 'message' | 'error' = 'message'): string | undefined => {
    const text = serverField(serverBody(error), field)
    return text?.trim() ? text : undefined
}

/**
 * What to tell the user when you have your own wording: the server's `message` (or `error`) when it sent
 * one, else `fallback` — never axios's "Request failed with status code 500". A plain thrown error (not
 * a request) keeps its own message.
 */
export const getApiErrorMessage = (error: unknown, fallback: string): string => {
    const body = serverBody(error)
    const text = serverField(body, 'message')?.trim() || serverField(body, 'error')?.trim()
    if (text) return text
    if (isRequestError(error)) return fallback
    return error instanceof Error && error.message ? error.message : fallback
}

/** The `data` a server attaches to an error it expects the page to act on (`{ success: false, data }`), else undefined. */
export const getErrorData = (error: unknown): Record<string, unknown> | undefined => {
    const body = serverBody(error)
    const data = isRecord(body) ? body.data : undefined
    return isRecord(data) ? data : undefined
}

/**
 * The machine-readable `errorCode` the SERVER sent with a refusal, or undefined.
 *
 * For a screen that must ACT differently on two refusals sharing a status: the message is copy and gets reworded,
 * the code is a contract (declared in `@numeo/types`, next to the DTOs of the endpoint that sends it).
 */
export const getServerErrorCode = (error: unknown): string | undefined => {
    if (!isAxiosError(error)) return undefined
    const data: unknown = error.response?.data
    return isRecord(data) && typeof data.errorCode === 'string' ? data.errorCode : undefined
}
